import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Button,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import type { EcommerceTabId } from '../../navigationConfig';
import {
  getGeneralConfig,
  getHeaderConfig,
  getContactSettings,
} from '../../../../services/ecommerceApi';
import {
  createDefaultWizardData,
  getDraftStorageKey,
  WIZARD_STEP_ORDER,
  type WizardData,
  type WizardStepId,
  type HeaderTemplateId,
} from './wizardTypes';
import {
  buildInstallSteps,
  runInstall,
  type InstallStepProgress,
} from './installEngine';
import StepGeneral from './steps/StepGeneral';
import StepTheme from './steps/StepTheme';
import StepPagesMenu from './steps/StepPagesMenu';
import StepContact from './steps/StepContact';
import StepProducts from './steps/StepProducts';
import StepPaymentShipping from './steps/StepPaymentShipping';
import StepLegal from './steps/StepLegal';
import StepSummary from './steps/StepSummary';

interface SiteSetupWizardProps {
  companyId: string | null;
  configId: string;
  onNavigate?: (tab: EcommerceTabId) => void;
}

const STEP_LABELS: Record<WizardStepId, string> = {
  general: 'Genel',
  theme: 'Tema',
  pagesMenu: 'Sayfalar & Menü',
  contact: 'İletişim',
  products: 'Ürün / Vitrin',
  paymentShipping: 'Ödeme & Teslimat',
  legal: 'Yasal',
};

const SUMMARY_INDEX = WIZARD_STEP_ORDER.length;

const StatusIcon: React.FC<{ status: InstallStepProgress['status'] }> = ({ status }) => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon color="success" />;
    case 'error':
      return <ErrorIcon color="error" />;
    case 'running':
      return <CircularProgress size={20} />;
    case 'skipped':
      return <RemoveCircleOutlineIcon color="disabled" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" />;
  }
};

const SiteSetupWizard: React.FC<SiteSetupWizardProps> = ({ companyId, configId, onNavigate }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [data, setData] = useState<WizardData>(() => createDefaultWizardData());
  const [initialized, setInitialized] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState<InstallStepProgress[]>([]);
  const [installDone, setInstallDone] = useState(false);
  const draftLoadedRef = useRef(false);

  const resolvedConfigId = configId || companyId || '';

  // Taslağı yükle veya sunucudan ön doldur.
  useEffect(() => {
    if (!companyId || draftLoadedRef.current) return;
    draftLoadedRef.current = true;

    const key = getDraftStorageKey(companyId);
    let restored = false;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<WizardData>;
        setData((prev) => ({ ...createDefaultWizardData(), ...prev, ...parsed }));
        restored = true;
      }
    } catch {
      restored = false;
    }

    if (restored) {
      setInitialized(true);
      return;
    }

    // Taslak yoksa mevcut ayarlardan ön doldur.
    (async () => {
      try {
        const [generalRes, headerRes, contactRes] = await Promise.allSettled([
          getGeneralConfig(companyId),
          getHeaderConfig(companyId),
          resolvedConfigId
            ? getContactSettings(companyId, resolvedConfigId)
            : Promise.resolve(null),
        ]);

        setData((prev) => {
          const next = { ...prev };
          if (generalRes.status === 'fulfilled') {
            const g: any = (generalRes.value as any)?.data ?? {};
            next.general = {
              ...next.general,
              siteName: g.siteName || next.general.siteName,
              faviconUrl: g.faviconUrl || next.general.faviconUrl,
              primaryColor: g.primaryColor || next.general.primaryColor,
              secondaryColor: g.secondaryColor || next.general.secondaryColor,
              fontFamily: g.fontFamily || next.general.fontFamily,
              metaTitle: g.metaTitle || next.general.metaTitle,
              metaDescription: g.metaDescription || next.general.metaDescription,
            };
            if (g.layoutType) next.theme.layoutType = g.layoutType;
            if (g.containerMaxWidth) next.theme.containerMaxWidth = g.containerMaxWidth;
            if (typeof g.supportEnabled === 'boolean') next.pagesMenu.supportEnabled = g.supportEnabled;
          }
          if (headerRes.status === 'fulfilled') {
            const h: any = (headerRes.value as any)?.data ?? {};
            const style = (h.headerStyle || h.headerType) as HeaderTemplateId | undefined;
            if (style && /^header[1-7]$/.test(style)) next.theme.headerType = style;
          }
          if (contactRes.status === 'fulfilled' && contactRes.value) {
            const c: any = (contactRes.value as any)?.data ?? {};
            next.contact = {
              ...next.contact,
              address: c.contactAddress || next.contact.address,
              phone: c.contactPhone || next.contact.phone,
              email: c.contactEmail || next.contact.email,
              workingHours: c.contactWorkingHours || next.contact.workingHours,
            };
          }
          return next;
        });
      } catch {
        // yok say - varsayılanlarla devam
      } finally {
        setInitialized(true);
      }
    })();
  }, [companyId, resolvedConfigId]);

  // Taslağı kaydet.
  useEffect(() => {
    if (!companyId || !initialized) return;
    try {
      window.localStorage.setItem(getDraftStorageKey(companyId), JSON.stringify(data));
    } catch {
      // sessizce yoksay
    }
  }, [companyId, data, initialized]);

  const patchStep = useCallback(
    <K extends keyof WizardData>(key: K, patch: Partial<WizardData[K]>) => {
      setData((prev) => ({ ...prev, [key]: { ...(prev[key] as object), ...patch } }));
    },
    [],
  );

  const currentStepId = activeStep < SUMMARY_INDEX ? WIZARD_STEP_ORDER[activeStep] : null;

  const toggleSkip = useCallback(
    (stepId: WizardStepId, skipped: boolean) => {
      setData((prev) => ({ ...prev, skipped: { ...prev.skipped, [stepId]: skipped } }));
    },
    [],
  );

  const handleInstall = useCallback(async () => {
    if (!companyId) return;
    setInstalling(true);
    setInstallDone(false);
    const steps = buildInstallSteps(companyId, resolvedConfigId, data);
    const { allSucceeded } = await runInstall(steps, setInstallProgress);
    setInstalling(false);
    setInstallDone(true);
    if (allSucceeded && companyId) {
      try {
        window.localStorage.removeItem(getDraftStorageKey(companyId));
      } catch {
        // yoksay
      }
    }
  }, [companyId, resolvedConfigId, data]);

  const hasErrors = installProgress.some((p) => p.status === 'error');

  const renderStepContent = () => {
    switch (currentStepId) {
      case 'general':
        return <StepGeneral value={data.general} onChange={(p) => patchStep('general', p)} />;
      case 'theme':
        return <StepTheme value={data.theme} onChange={(p) => patchStep('theme', p)} />;
      case 'pagesMenu':
        return <StepPagesMenu value={data.pagesMenu} onChange={(p) => patchStep('pagesMenu', p)} />;
      case 'contact':
        return <StepContact value={data.contact} onChange={(p) => patchStep('contact', p)} />;
      case 'products':
        return <StepProducts value={data.products} onChange={(p) => patchStep('products', p)} />;
      case 'paymentShipping':
        return (
          <StepPaymentShipping
            value={data.paymentShipping}
            onChange={(p) => patchStep('paymentShipping', p)}
          />
        );
      case 'legal':
        return <StepLegal value={data.legal} onChange={(p) => patchStep('legal', p)} />;
      default:
        return <StepSummary data={data} onEditStep={(i) => setActiveStep(i)} />;
    }
  };

  const stepperSteps = useMemo(
    () => [...WIZARD_STEP_ORDER.map((id) => STEP_LABELS[id]), 'Özet & Kur'],
    [],
  );

  if (!companyId) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Şirket bilgisi yüklenemedi. Kurulum sihirbazını kullanmak için lütfen tekrar deneyin.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <RocketLaunchIcon color="primary" />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Site Kurulum Sihirbazı
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adımları doldurun, "Kur" deyince siteniz tek tıkla hazırlanır.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {stepperSteps.map((label, index) => {
            const stepId = index < SUMMARY_INDEX ? WIZARD_STEP_ORDER[index] : null;
            const skipped = stepId ? data.skipped[stepId] : false;
            return (
              <Step key={label} completed={false}>
                <StepButton color="inherit" onClick={() => !installing && setActiveStep(index)}>
                  <StepLabel optional={skipped ? <Typography variant="caption">Atlandı</Typography> : undefined}>
                    {label}
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        {!initialized ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : installDone ? (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {hasErrors ? 'Kurulum kısmen tamamlandı' : 'Kurulum tamamlandı 🎉'}
            </Typography>
            <Alert severity={hasErrors ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {hasErrors
                ? 'Bazı adımlar kaydedilemedi. Hatalı adımları tekrar deneyebilirsiniz.'
                : 'Tüm ayarlar panele işlendi. Siteniz yayına hazır.'}
            </Alert>
            <List dense>
              {installProgress.map((p) => (
                <ListItem key={p.id}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <StatusIcon status={p.status} />
                  </ListItemIcon>
                  <ListItemText
                    primary={p.label}
                    secondary={p.status === 'error' ? p.error : p.status === 'skipped' ? 'Atlandı' : undefined}
                  />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              {hasErrors && (
                <Button variant="contained" onClick={handleInstall} disabled={installing}>
                  Hatalı adımları tekrar dene
                </Button>
              )}
              <Button variant="outlined" onClick={() => onNavigate?.('homepage')}>
                Ana Sayfa Ayarlarına Git
              </Button>
              <Button
                onClick={() => {
                  setInstallDone(false);
                  setActiveStep(0);
                }}
              >
                Sihirbazı yeniden aç
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            {currentStepId && (
              <FormControlLabel
                sx={{ mb: 1, float: 'right' }}
                control={
                  <Checkbox
                    checked={data.skipped[currentStepId]}
                    onChange={(e) => toggleSkip(currentStepId, e.target.checked)}
                  />
                }
                label="Bu adımı atla"
              />
            )}

            <Box sx={{ opacity: currentStepId && data.skipped[currentStepId] ? 0.5 : 1, pointerEvents: currentStepId && data.skipped[currentStepId] ? 'none' : 'auto' }}>
              {renderStepContent()}
            </Box>

            {installing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Kuruluyor...
                </Typography>
                <LinearProgress />
                <List dense>
                  {installProgress.map((p) => (
                    <ListItem key={p.id}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <StatusIcon status={p.status} />
                      </ListItemIcon>
                      <ListItemText primary={p.label} secondary={p.status === 'error' ? p.error : undefined} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" justifyContent="space-between">
              <Button
                disabled={activeStep === 0 || installing}
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              >
                Geri
              </Button>

              {activeStep < SUMMARY_INDEX ? (
                <Button variant="contained" onClick={() => setActiveStep((s) => s + 1)}>
                  İleri
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<RocketLaunchIcon />}
                  onClick={handleInstall}
                  disabled={installing}
                >
                  Kur
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SiteSetupWizard;
